import { useEffect, useRef, useState } from "react";
import { Bell, AlertTriangle, Truck, HandCoins, CalendarClock, CheckCircle2, Wrench, Headphones, Cake, UserCheck, AlarmClock } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";

const META = {
  rmv_pending: { icon: AlertTriangle, cls: "text-warning", key: "notif_rmv" },
  stuck_delivery: { icon: Truck, cls: "text-primary", key: "notif_stuck" },
  receivables_overdue: { icon: HandCoins, cls: "text-primary", key: "notif_overdue" },
  receivables_due_today: { icon: CalendarClock, cls: "text-warning", key: "notif_due_today" },
  revision_ready: { icon: Wrench, cls: "text-success", key: "notif_revision_ready" },
  new_leads: { icon: Headphones, cls: "text-cyan-400", key: "notif_new_leads" },
  assigned_leads: { icon: UserCheck, cls: "text-emerald-400", key: "notif_assigned_leads" },
  stale_leads: { icon: AlarmClock, cls: "text-orange-400", key: "notif_stale_leads" },
  birthdays: { icon: Cake, cls: "text-pink-400", key: "notif_birthdays" },
};

export default function NotificationBell({ onNavigate }) {
  const { t } = useI18n();
  const [data, setData] = useState({ total: 0, groups: [] });
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications/summary");
      setData(r.data || { total: 0, groups: [] });
    } catch { /* silent — bell just stays empty */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 border border-border bg-background/90 backdrop-blur-sm hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
        aria-label={t("notif_title")}
      >
        <Bell size={17} />
        {data.total > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 bg-primary text-white font-display font-black text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ring-2 ring-background animate-pulse"
          >
            {data.total > 99 ? "99+" : data.total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[min(92vw,380px)] max-h-[70vh] overflow-y-auto bg-background border border-border shadow-2xl z-50"
        >
          <div className="px-4 py-3 border-b border-border bg-surface flex items-center justify-between">
            <p className="label-eyebrow text-primary">{t("notif_title")}</p>
            {data.total > 0 && (
              <span className="text-[10px] uppercase tracking-widest text-text-secondary">{data.total}</span>
            )}
          </div>

          {data.groups.length === 0 ? (
            <div data-testid="notification-empty" className="text-center py-10 px-4">
              <CheckCircle2 size={32} className="mx-auto text-success/50 mb-3" />
              <p className="text-text-secondary text-sm">{t("notif_empty")}</p>
            </div>
          ) : (
            data.groups.map((g) => {
              const m = META[g.type] || META.rmv_pending;
              const Icon = m.icon;
              return (
                <button
                  key={g.type}
                  data-testid={`notif-group-${g.type}`}
                  onClick={() => { setOpen(false); onNavigate && onNavigate(g.tab); }}
                  className="w-full text-left px-4 py-3 border-b border-border hover:bg-surface transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={14} className={m.cls} />
                    <span className={`text-xs font-display font-bold uppercase tracking-widest ${m.cls}`}>
                      {t(m.key)}
                    </span>
                    <span className="ml-auto text-[10px] font-display font-black bg-primary/15 text-primary px-2 py-0.5 rounded-full">
                      {g.count}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {(g.items || []).slice(0, 4).map((it, i) => (
                      <p key={i} className="text-xs text-text-secondary truncate">
                        • {it.label}
                        {it.store ? ` · ${it.store}` : ""}
                        {it.source ? ` · ${it.source}` : ""}
                        {it.buyer ? ` · ${it.buyer}` : ""}
                        {typeof it.days === "number" && it.days > 0 ? ` · ${it.days}d` : ""}
                        {it.due_date && g.type === "receivables_overdue" ? ` · ${it.due_date}` : ""}
                      </p>
                    ))}
                    {g.count > 4 && <p className="text-[10px] text-text-secondary/70">+{g.count - 4}…</p>}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
